define('viewModels/privileged/analytics/dataControllers/AggregateAnalyticsLiveDataController',[
    'lib/lodash4',
    'lib/knockout',
    'lib/bluebird',

    'models/AnalyticsData',

    'services/analyticsService',
    'services/loggingService',
    'services/notificationService',

    'viewModels/ViewModel'
], function (
    _,
    ko,
    Promise,

    AnalyticsData,

    analyticsService,
    LOG,
    notificationService,

    ViewModel
) {
    'use strict';

    // ===================================================================================
    // Util Functions
    // ===================================================================================

    var fillMetricsCollection = function (newMetricsCollection, destinationCollection) {
        _.each(newMetricsCollection, function (metricsObj) {
            var newId = metricsObj.entityId();

            var existingModel = _.find(destinationCollection, function (model) {
                return model.entityId() === newId;
            });

            if (existingModel) {
                AnalyticsData.fill(existingModel, metricsObj);
            } else {
                destinationCollection.push(metricsObj);
            }
        });

        return destinationCollection;
    };

    var unsubscribeFromNotifications = function (obj) {
        if (!_.isEmpty(obj)) {
            return notificationService.batchUnsubscribe(obj);
        }

        return Promise.resolve();
    };

    var subscribeToNotifications = function (obj) {
        // Subscribe to live data and retrieve existing data. If notificationService
        // is not connected, then skip the subscription process.
        if (notificationService.connected() && !_.isEmpty(obj)) {
            return notificationService.batchSubscribe(obj);
        }

        return Promise.resolve();
    };

    // ===================================================================================
    // Class Definition
    // ===================================================================================

    /**
     * A data controller class that provides a core set of methods to load analytics data
     * model objects for collections of ids and return objects that will be used in views
     * for the end user. Instances of this class will manage the configuration of live
     * data subscriptions for any requested ids and the clean up the subscriptions when
     * the lifecycle of an instance ends.
     *
     * @param {Function} disjunctionQueryFunction - A reference to the function that will
     *  be executed to query the analytics backend APIs for data. The expected arguments
     *  passed to this function are:
     *
     *     - {String[]} idArr - a collection of strings representing the IDs to query
     *     - {String}   interval - an ISO8601 time interval string
     *     - {Object}   options.mediaType - a string representing the media type of the
     *        data to be returned.
     *
     *  The return value of this function should be a Promise.
     *
     * @param {Function} topicGeneratorFunction - A function that will return a String
     *  that represents the name of the topic to subscribe to for notifications.
     *
     * @return {QueueAnalyticsDataController}
     */
    var AggregateAnalyticsLiveDataController = ViewModel.extend(function (disjunctionQueryFunction, topicGeneratorFunction) {
        //
        // Private Variables
        //

        var self = this;

        // Keep track of existing subscriptions to know what to unsubscribe from when this
        // class is disposed.
        var existingSubscriptions = {};

        // The current metric collection that is loaded into the instance. This will be
        // used to determine which AnalyticsData instances to update when notifications
        // are received.
        var metricDataCollection = [];

        // The most recent new data handler that has been registered by the
        // getLiveAnalyticsData arguments. This is called on new notifications so the
        // consumer can react to the update.
        var onNewData;

        /**
         * Send a quest to load analytics data from the backend.
         *
         * @param  {String[]} idArr - The collection of queue IDs to request
         * @param  {String} interval - The ISO 8601 interval to get data for
         * @param  {Object[]} destinationCollection - The collection to place the
         *   resulting data in to.
         * @param  {Object} [opts=undefined] An object containing optional arguments
         * @param  {String} [opts.mediaType="voice"] The media type to request data for.
         * @return {Promise} A promise that will resolve with a reference to the updated
         *  destination collection.
         */
        var loadAnalyticsData = function (idArr, interval, destinationCollection, options) {
            if (idArr.length === 0) {
                return Promise.resolve([]);
            }

            return disjunctionQueryFunction(idArr, interval, options)
                .then(function (response) {
                    return fillMetricsCollection(response, destinationCollection);
                });
        };

        /**
         * Respond to notifications for the registered topics.
         *
         * @param  {Object} data The data contained in the notification.
         */
        var onStatNotification = function (data) {
            // because of new metrics, we now have some observational data that is in the same stats topic
            // observations will have no media types, aggregate data will always have a type

            var dataId, existingAnalyticsModel, newModel;
            if (data.group) {
                if (_.isUndefined(data.group.mediaType) || data.group.mediaType === self.mediaType.peek()) {
                    dataId = data.group.queueId || data.group.userId;
                    if (dataId) {
                        existingAnalyticsModel = _.find(metricDataCollection, function (model) {
                            return model.entityId() === dataId;
                        });

                        if (existingAnalyticsModel) {
                            AnalyticsData.merge(existingAnalyticsModel, data.data[0]);
                        } else {
                            data.data[0].entity = { "id": dataId };
                            newModel = new AnalyticsData(data.data[0]);
                            metricDataCollection.push(newModel);

                            if (onNewData) {
                                onNewData(newModel);
                            }
                        }
                    }
                }
            } else {
                if (_.isUndefined(data.mediaType) || data.mediaType === self.mediaType.peek()) {
                    dataId = data.entity.id;

                    existingAnalyticsModel = _.find(metricDataCollection, function (model) {
                        return model.entityId() === dataId;
                    });

                    if (existingAnalyticsModel) {
                        AnalyticsData.merge(existingAnalyticsModel, data);
                    } else {
                        data.entity = { "id": dataId };
                        newModel = new AnalyticsData(data);
                        metricDataCollection.push(newModel);

                        if (onNewData) {
                            onNewData(newModel);
                        }

                    }
                }
            }
        };

        //
        // Cleanup
        //

        self.onDispose = function () {
            unsubscribeFromNotifications(existingSubscriptions)
                .catch(function () {
                    LOG.warn("Failed to clean up the existing subscriptions of the QueueAnalyticsDataController", self, existingSubscriptions);
                })
                .finally(function () {
                    existingSubscriptions = {};
                });
        };

        //
        // Public Attributes
        //

        /**
         * A setter/getter whose value will be used to determine the media type of metrics
         *  that will be returned from the backend. The value passed here will be the
         *  default value used in making these requests.
         *
         * @type {String}
         */
        self.mediaType = ko.observable(analyticsService.MEDIA_TYPES.VOICE);

        //
        // Public Methods
        //

        /**
         * Load a live collection of AnalyticsData objects for the provided collection of
         * IDs. This method will handle subscribing the provided IDs to receive stat
         * notifications from the backend.
         *
         * @param  {String[]} idCollection - The collection of ids to request.
         * @param  {String} interval - The ISO 8601 interval to request data for.
         * @param  {Function} onNewDataHandler - A function that will be executed each time
         *  a new AnalyticsData object is created from an incoming notification.
         * @param  {Object} [opts=undefined] - An object containing optional arguments to
         *  send alongside the request to load the analytics data from the backend.
         *
         * @return {Promise} A promise object that will resolve to contain an array of
         *  AnalyticsData objects that were loaded for the provided arguments. This
         *  collection may not contain data for all requested IDs, it will only contain
         *  AnalyticsData instances for IDs that had data recorded for them. In lieu of
         *  this, when a notification is received for an AnalyticsData object that does
         *  not exist, the function passed in as the "onNewDataHandler" will be called
         *  with the new live instance.
         */
        self.getLiveAnalyticsData = function (idCollection, interval, onNewDataHandler, opts) {
            var options = _.assign({
                mediaType: self.mediaType.peek()
            }, opts);

            metricDataCollection = [];
            onNewData = onNewDataHandler;

            //
            // Live Data Setup
            //

            // Configure the topic subscription object argument
            var queueStatTopics = _.reduce(idCollection, function (result, id) {
                var topicKey = topicGeneratorFunction(id);

                if (!_.has(existingSubscriptions, topicKey)) {
                    // Only add new topic subscriptions to the object since they all
                    // react to the same callback.
                    result[topicKey] = onStatNotification;
                }

                return result;
            }, {});

            existingSubscriptions = _.defaults(existingSubscriptions, queueStatTopics);

            return subscribeToNotifications(queueStatTopics)
                            .then(function () {
                                return loadAnalyticsData(
                                    idCollection,
                                    interval,
                                    metricDataCollection,
                                    options
                                );
                            })
                            .catch(function (e) {
                                LOG.warn("Unable to subscribe to live analytics notifications.", e);
                                // still load the data, even if we fail to subscribe
                                return loadAnalyticsData(
                                    idCollection,
                                    interval,
                                    metricDataCollection,
                                    options
                                );
                            });
        };

        self.getLiveAnalyticsObservations = function (idCollection, interval, onNewDataHandler, options) {
            metricDataCollection = [];
            onNewData = onNewDataHandler;
            //
            // Live Data Setup
            //

            // Configure the topic subscription object argument
            var queueStatTopics = _.reduce(idCollection, function (result, id) {
                var topicKey = topicGeneratorFunction(id);
                if (_.isArray(topicKey)) {
                    _.each(topicKey, function (topic) {
                        if (!_.has(existingSubscriptions, topic)) {
                            result[topic] = onStatNotification;
                        }
                    });
                } else {
                    if (!_.has(existingSubscriptions, topicKey)) {
                        // Only add new topic subscriptions to the object since they all
                        // react to the same callback.
                        result[topicKey] = onStatNotification;
                    }
                }

                return result;
            }, {});

            existingSubscriptions = _.defaults(existingSubscriptions, queueStatTopics);

            return subscribeToNotifications(queueStatTopics)
                            .then(function () {
                                return loadAnalyticsData(
                                    idCollection,
                                    interval,
                                    metricDataCollection,
                                    options
                                );
                            })
                            .catch(function (e) {
                                LOG.warn("Unable to subscribe to live analytics notifications.", e);
                                // still load the data, even if we fail to subscribe
                                return loadAnalyticsData(
                                    idCollection,
                                    interval,
                                    metricDataCollection,
                                    options
                                );
                            });
        };

        /**
         * Load a static collection of AnalyticsData objects for the provided collection of
         * IDs. The requested IDs will not receive live notifications when updates occur.
         *
         * @param  {String[]} idCollection - The collection of ids to request.
         * @param  {String} interval - The ISO 8601 interval to request data for.
         * @param  {Object} [opts=undefined] - An object containing optional arguments to
         *  send alongside the request to load the analytics data from the backend.
         *
         * @return {Promise} A promise object that will resolve to contain an array of
         *  AnalyticsData objects that were loaded for the provided arguments. This
         *  collection may not contain data for all requested IDs, it will only contain
         *  AnalyticsData instances for IDs that had data recorded for them.
         */
        self.getStaticAnalyticsData = function (idCollection, interval, opts) {
            var options = _.assign({
                mediaType: self.mediaType.peek()
            }, opts);

            return loadAnalyticsData(idCollection, interval, [], options);
        };

        /**
         * Given a collection of IDs, unsubscribe each ID from live data notifications.
         *
         * @param  {String[]} idCollection - An array of string IDs to unsubscribe from
         *  live data
         * @return {Promise} A promise that will resolve when the request to unsubscribe
         *  from live data updates has completed. No data will be passed to the handlers
         *  of this promise.
         */
        self.unsubscribeIdsFromLiveData = function (idCollection) {
            var subscriptions = _.reduce(idCollection, function (result, id) {
                // convert the collection of Queue IDs into an object of topic keys and
                // the callback associated with that key.
                var topicKey = topicGeneratorFunction(id);

                if (_.has(existingSubscriptions, topicKey)) {
                    // If an existing subscription is found, grab it and remove it from
                    // the long-living collection of topics
                    result[topicKey] = existingSubscriptions[topicKey];
                }

                return result;
            }, {});

            return unsubscribeFromNotifications(subscriptions)
                    .then(function () {
                        if (!_.isEmpty(subscriptions)) {
                            var unsubscribedTopicKeys = _.keys(subscriptions);
                            existingSubscriptions = _.omit(existingSubscriptions, unsubscribedTopicKeys);
                        }
                    });
        };

        //
        // Computed
        //

        self.computed(function () {
            if (!notificationService.connected()) {
                // remove all subscriptions if notificationService gets disconnected
                unsubscribeFromNotifications(existingSubscriptions);
            }
        });
    });

    return AggregateAnalyticsLiveDataController;
});

